import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { PageHeader } from "../components/PageHeader";
import VerificaNormativa from "../components/VerificaNormativa";
import { Plus, Trash, PencilSimple, LockKey, HardHat, CurrencyEur, Check, X } from "@phosphor-icons/react";
import { eur } from "../lib/impresa";

const fmt = eur;

export default function Impostazioni() {
  const [tipi, setTipi] = useState([]);
  const [macroaree, setMacroaree] = useState([]);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    tipo: "", label: "", macroarea: "altro", fasi: [""], checklist: [""], scadenze: [], prezzo_standard: 0, spese_standard: 0, descrizione: "", cantiere: false, voci_template: [],
  });

  const load = async () => {
    const { data } = await api.get("/settings/tipi-incarico");
    setTipi(data);
    api.get("/settings/macroaree").then((r) => setMacroaree(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const openNew = () => {
    setEditing(null);
    setForm({ tipo: "", label: "", macroarea: "altro", fasi: [""], checklist: [""], scadenze: [], prezzo_standard: 0, spese_standard: 0, descrizione: "", cantiere: false, voci_template: [] });
    setOpen(true);
  };
  const openEdit = (t) => {
    setEditing(t);
    setForm({
      tipo: t.tipo,
      label: t.label,
      macroarea: t.macroarea || "altro",
      fasi: t.fasi?.length ? [...t.fasi] : [""],
      checklist: t.checklist?.length ? [...t.checklist] : [""],
      scadenze: t.scadenze?.length ? t.scadenze.map((s) => ({ nome: s.nome || "", giorni: s.giorni || 0, note: s.note || "" })) : [],
      prezzo_standard: t.prezzo_standard || 0,
      spese_standard: t.spese_standard || 0,
      descrizione: t.descrizione || "",
      cantiere: !!t.cantiere,
      voci_template: t.voci_template?.length ? t.voci_template.map((v) => ({ titolo: v.titolo || "", descrizione: v.descrizione || "", onorario: v.onorario || 0, spese: v.spese || 0 })) : [],
    });
    setOpen(true);
  };
  const del = async (t) => {
    if (t.is_builtin) { toast.error("Non puoi eliminare un tipo predefinito"); return; }
    if (!window.confirm(`Eliminare il tipo "${t.label}"?`)) return;
    try { await api.delete(`/settings/tipi-incarico/${t.id}`); toast.success("Eliminato"); load(); } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  const submit = async (e) => {
    e.preventDefault();
    const payload = {
      tipo: form.tipo.trim().toLowerCase().replace(/\s+/g, "_"),
      label: form.label.trim(),
      macroarea: form.macroarea || "altro",
      fasi: form.fasi.filter((f) => f.trim()),
      checklist: form.checklist.filter((c) => c.trim()),
      scadenze: form.scadenze.filter((s) => (s.nome || "").trim()),
      prezzo_standard: parseFloat(form.prezzo_standard) || 0,
      spese_standard: parseFloat(form.spese_standard) || 0,
      descrizione: form.descrizione || "",
      cantiere: !!form.cantiere,
      voci_template: (form.voci_template || []).filter((v) => (v.titolo || "").trim()).map((v) => ({
        titolo: v.titolo.trim(),
        descrizione: v.descrizione || "",
        onorario: parseFloat(v.onorario) || 0,
        spese: parseFloat(v.spese) || 0,
      })),
    };
    try {
      if (editing) await api.put(`/settings/tipi-incarico/${editing.id}`, payload);
      else await api.post("/settings/tipi-incarico", payload);
      toast.success("Salvato"); setOpen(false); setEditing(null); load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore");
    }
  };

  return (
    <div className="p-4 md:p-8 w-full max-w-[1400px] mx-auto" data-testid="impostazioni-page">
      <PageHeader
        eyebrow="Configurazione studio"
        title="Tipi Incarico & Prezzario"
        description="Crea e modifica i tipi di incarico. Fasi, checklist, scadenze legali, prezzo standard e voci prezzario verranno applicati automaticamente ai nuovi incarichi e preventivi."
        actions={<Button onClick={openNew} data-testid="new-tipo-button"><Plus size={16} weight="bold" /> Nuovo tipo</Button>}
      />

      <VerificaNormativa onApplied={load} />

      <MacroareeManager macroaree={macroaree} onChange={() => api.get("/settings/macroaree").then((r) => setMacroaree(r.data)).catch(() => {})} />

      <Card className="border-slate-200 shadow-none overflow-hidden">
        <div className="p-5 border-b border-slate-200">
          <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-1">Tipi di incarico ({tipi.length})</div>
          <h3 className="font-heading text-lg font-semibold">Prezzario &amp; Template · organizzati per macroarea</h3>
        </div>
        <div className="divide-y divide-slate-200" data-testid="tipi-grouped">
          {macroaree.map((ma) => {
            const tipiInMa = tipi.filter((t) => (t.macroarea || "altro") === ma.slug);
            if (tipiInMa.length === 0) return null;
            return (
              <div key={ma.slug} data-testid={`macroarea-group-${ma.slug}`}>
                <div className="px-5 py-3 bg-slate-200 border-y border-slate-300 flex items-center justify-between">
                  <div className="text-sm font-heading font-bold text-slate-900 uppercase tracking-wide">{ma.label}</div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-slate-600 bg-white/70 px-2 py-0.5 rounded-full border border-slate-300">{tipiInMa.length} tipi</div>
                </div>
                <div className="divide-y divide-slate-100 pl-6 border-l-4 border-slate-300 ml-3">
                  {tipiInMa.map((t) => (
                    <div key={t.id} className="flex items-center gap-4 p-4 hover:bg-slate-50" data-testid={`tipo-${t.id}`}>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <div className="font-medium text-slate-900">{t.label}</div>
                          <span className="text-[10px] font-mono uppercase tracking-widest text-slate-500">{t.tipo}</span>
                          {t.cantiere && <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200"><HardHat size={10} /> Cantiere</span>}
                        </div>
                        <div className="text-xs text-slate-500 mt-1 flex items-center gap-3 flex-wrap">
                          <span>{(t.fasi || []).length} fasi</span>
                          <span>{(t.checklist || []).length} doc</span>
                          <span>{(t.scadenze || []).length} scadenze legali</span>
                          {t.prezzo_standard > 0 && <span className="inline-flex items-center gap-1 font-medium text-slate-700"><CurrencyEur size={11} /> {fmt(t.prezzo_standard)}</span>}
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button variant="ghost" size="sm" onClick={() => openEdit(t)} data-testid={`edit-tipo-${t.id}`}><PencilSimple size={14} /></Button>
                        <Button variant="ghost" size="sm" onClick={() => del(t)} data-testid={`delete-tipo-${t.id}`}><Trash size={14} className="text-red-600" /></Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? `Modifica: ${editing.label}` : "Nuovo tipo di incarico"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-5">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Etichetta</Label>
                <Input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} required placeholder="es. Certificazione Energetica" data-testid="tipo-label-input" />
              </div>
              <div className="space-y-2">
                <Label>Slug identificativo</Label>
                <Input value={form.tipo} onChange={(e) => setForm({ ...form, tipo: e.target.value })} required disabled={!!editing} placeholder="es. certificazione_energetica" data-testid="tipo-slug-input" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Macroarea</Label>
              <Select value={form.macroarea} onValueChange={(v) => setForm({ ...form, macroarea: v })}>
                <SelectTrigger data-testid="tipo-macroarea-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {macroaree.map((m) => <SelectItem key={m.slug} value={m.slug}>{m.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-slate-500">Le macroaree organizzano i tipi in categorie logiche (Pratiche Edilizie, Catasto, ecc.) — la scegli quando crei un nuovo incarico o preventivo.</p>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-2">
                <Label>Onorario standard (€)</Label>
                <Input type="number" step="0.01" value={form.prezzo_standard} onChange={(e) => setForm({ ...form, prezzo_standard: e.target.value })} data-testid="tipo-prezzo-input" />
              </div>
              <div className="space-y-2">
                <Label>Spese standard (€)</Label>
                <Input type="number" step="0.01" value={form.spese_standard} onChange={(e) => setForm({ ...form, spese_standard: e.target.value })} data-testid="tipo-spese-input" />
              </div>
              <label className="flex items-end gap-2 pb-2 text-sm cursor-pointer">
                <input type="checkbox" checked={form.cantiere} onChange={(e) => setForm({ ...form, cantiere: e.target.checked })} className="h-4 w-4" data-testid="tipo-cantiere-checkbox" />
                Mostra reparto Cantiere sull&apos;incarico
              </label>
            </div>
            <div className="space-y-2">
              <Label>Descrizione standard <span className="text-xs text-slate-500 font-normal">(appare nella voce del preventivo/fattura)</span></Label>
              <Textarea value={form.descrizione} onChange={(e) => setForm({ ...form, descrizione: e.target.value })} rows={3} placeholder="Es. Rilievo dell'unità immobiliare; Restituzione grafica; Documenti di corredo; Predisposizione ed invio pratica edilizia CILA." data-testid="tipo-descrizione-input" />
            </div>

            <Tabs defaultValue="fasi" className="w-full">
              <TabsList>
                <TabsTrigger value="fasi" data-testid="tab-tipo-fasi">Fasi ({form.fasi.filter((f) => f.trim()).length})</TabsTrigger>
                <TabsTrigger value="checklist" data-testid="tab-tipo-checklist">Checklist ({form.checklist.filter((c) => c.trim()).length})</TabsTrigger>
                <TabsTrigger value="scadenze" data-testid="tab-tipo-scadenze">Scadenze legali ({form.scadenze.length})</TabsTrigger>
                <TabsTrigger value="voci" data-testid="tab-tipo-voci">Voci prezzario ({(form.voci_template || []).length})</TabsTrigger>
              </TabsList>

              <TabsContent value="fasi" className="mt-3 space-y-2">
                {form.fasi.map((f, i) => (
                  <div key={i} className="flex gap-2 items-center" data-testid={`fase-row-${i}`}>
                    <span className="text-xs font-mono text-slate-400 w-6">#{i + 1}</span>
                    <Input value={f} onChange={(e) => { const arr = [...form.fasi]; arr[i] = e.target.value; setForm({ ...form, fasi: arr }); }} placeholder="Nome fase" />
                    <Button type="button" variant="ghost" size="sm" onClick={() => setForm({ ...form, fasi: form.fasi.filter((_, j) => j !== i) })}><Trash size={12} className="text-red-600" /></Button>
                  </div>
                ))}
                <Button type="button" variant="outline" size="sm" onClick={() => setForm({ ...form, fasi: [...form.fasi, ""] })}><Plus size={12} /> Aggiungi fase</Button>
              </TabsContent>

              <TabsContent value="checklist" className="mt-3 space-y-2">
                {form.checklist.map((c, i) => (
                  <div key={i} className="flex gap-2 items-center" data-testid={`checklist-row-${i}`}>
                    <Input value={c} onChange={(e) => { const arr = [...form.checklist]; arr[i] = e.target.value; setForm({ ...form, checklist: arr }); }} placeholder="Documento richiesto" />
                    <Button type="button" variant="ghost" size="sm" onClick={() => setForm({ ...form, checklist: form.checklist.filter((_, j) => j !== i) })}><Trash size={12} className="text-red-600" /></Button>
                  </div>
                ))}
                <Button type="button" variant="outline" size="sm" onClick={() => setForm({ ...form, checklist: [...form.checklist, ""] })}><Plus size={12} /> Aggiungi voce</Button>
              </TabsContent>

              <TabsContent value="scadenze" className="mt-3 space-y-2">
                {form.scadenze.map((s, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`scadenza-row-${i}`}>
                    <Input className="col-span-5" placeholder="Nome scadenza" value={s.nome} onChange={(e) => { const arr = [...form.scadenze]; arr[i] = { ...arr[i], nome: e.target.value }; setForm({ ...form, scadenze: arr }); }} />
                    <Input className="col-span-2" type="number" placeholder="gg" value={s.giorni} onChange={(e) => { const arr = [...form.scadenze]; arr[i] = { ...arr[i], giorni: parseInt(e.target.value) || 0 }; setForm({ ...form, scadenze: arr }); }} />
                    <Input className="col-span-4" placeholder="Note (opzionale)" value={s.note} onChange={(e) => { const arr = [...form.scadenze]; arr[i] = { ...arr[i], note: e.target.value }; setForm({ ...form, scadenze: arr }); }} />
                    <Button type="button" variant="ghost" size="sm" className="col-span-1" onClick={() => setForm({ ...form, scadenze: form.scadenze.filter((_, j) => j !== i) })}><Trash size={12} className="text-red-600" /></Button>
                  </div>
                ))}
                <Button type="button" variant="outline" size="sm" onClick={() => setForm({ ...form, scadenze: [...form.scadenze, { nome: "", giorni: 0, note: "" }] })}><Plus size={12} /> Aggiungi scadenza</Button>
                <p className="text-[11px] text-slate-500 mt-2">Le scadenze si calcolano automaticamente sommando i giorni alla data di creazione dell&apos;incarico.</p>
              </TabsContent>

              <TabsContent value="voci" className="mt-3 space-y-2">
                <p className="text-[11px] text-slate-500">Definisci le voci standard del prezzario per questo tipo. Nel preventivo potrai aggiungerle con un click e modificarle liberamente.</p>
                {(form.voci_template || []).map((v, i) => (
                  <div key={i} className="border border-slate-200 rounded-md p-3 space-y-2" data-testid={`voce-template-row-${i}`}>
                    <div className="grid grid-cols-6 gap-2">
                      <div className="col-span-4 space-y-1">
                        <Label className="text-xs">Titolo (grassetto nel PDF)</Label>
                        <Input value={v.titolo} onChange={(e) => { const arr = [...form.voci_template]; arr[i] = { ...arr[i], titolo: e.target.value }; setForm({ ...form, voci_template: arr }); }} placeholder="Es. CILA, Direzione Lavori..." data-testid={`voce-template-titolo-${i}`} />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Onorario €</Label>
                        <Input type="number" step="0.01" value={v.onorario} onChange={(e) => { const arr = [...form.voci_template]; arr[i] = { ...arr[i], onorario: e.target.value }; setForm({ ...form, voci_template: arr }); }} className="font-mono text-right" data-testid={`voce-template-onorario-${i}`} />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Spese €</Label>
                        <Input type="number" step="0.01" value={v.spese} onChange={(e) => { const arr = [...form.voci_template]; arr[i] = { ...arr[i], spese: e.target.value }; setForm({ ...form, voci_template: arr }); }} className="font-mono text-right" data-testid={`voce-template-spese-${i}`} />
                      </div>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Descrizione dettagliata</Label>
                      <Textarea rows={2} value={v.descrizione} onChange={(e) => { const arr = [...form.voci_template]; arr[i] = { ...arr[i], descrizione: e.target.value }; setForm({ ...form, voci_template: arr }); }} placeholder="Descrivi cosa comprende questa voce (apparirà sotto il titolo nel PDF)" data-testid={`voce-template-descr-${i}`} />
                    </div>
                    <div className="flex justify-end">
                      <Button type="button" variant="ghost" size="sm" onClick={() => setForm({ ...form, voci_template: form.voci_template.filter((_, j) => j !== i) })}><Trash size={12} className="text-red-600" /> Rimuovi</Button>
                    </div>
                  </div>
                ))}
                <Button type="button" variant="outline" size="sm" onClick={() => setForm({ ...form, voci_template: [...(form.voci_template || []), { titolo: "", descrizione: "", onorario: 0, spese: 0 }] })} data-testid="add-voce-template-button"><Plus size={12} /> Aggiungi voce prezzario</Button>
              </TabsContent>
            </Tabs>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
              <Button type="submit" data-testid="tipo-submit-button">Salva</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function MacroareeManager({ macroaree, onChange }) {
  const [newLabel, setNewLabel] = useState("");
  const [editSlug, setEditSlug] = useState(null);
  const [editLabel, setEditLabel] = useState("");

  const add = async () => {
    const label = newLabel.trim();
    if (!label) return;
    try { await api.post("/settings/macroaree", { label }); setNewLabel(""); onChange(); toast.success("Macroarea aggiunta"); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  const startEdit = (m) => { setEditSlug(m.slug); setEditLabel(m.label); };
  const saveEdit = async () => {
    const label = editLabel.trim();
    if (!label) return;
    try { await api.put(`/settings/macroaree/${editSlug}`, { label }); setEditSlug(null); onChange(); toast.success("Aggiornata"); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  const del = async (m) => {
    if (m.slug === "altro") { toast.error("Non puoi eliminare 'Altro'"); return; }
    if (!window.confirm(`Eliminare "${m.label}"? I tipi collegati verranno spostati in "Altro".`)) return;
    try { await api.delete(`/settings/macroaree/${m.slug}`); onChange(); toast.success("Eliminata"); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  return (
    <Card className="border-slate-200 shadow-none overflow-hidden mb-6" data-testid="macroaree-manager">
      <div className="p-5 border-b border-slate-200">
        <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-1">Categorie ({macroaree.length})</div>
        <h3 className="font-heading text-lg font-semibold">Macroaree</h3>
        <p className="text-xs text-slate-500 mt-1">Organizzano i tipi di incarico. Modifica il nome o aggiungine di nuove.</p>
      </div>
      <div className="divide-y divide-slate-100">
        {macroaree.map((m) => (
          <div key={m.slug} className="flex items-center gap-3 p-3 hover:bg-slate-50" data-testid={`macroarea-item-${m.slug}`}>
            {editSlug === m.slug ? (
              <>
                <Input value={editLabel} onChange={(e) => setEditLabel(e.target.value)} className="h-8 text-sm flex-1" autoFocus onKeyDown={(e) => e.key === "Enter" && saveEdit()} data-testid={`macroarea-edit-input-${m.slug}`} />
                <Button size="sm" onClick={saveEdit} className="h-8 w-8 p-0" data-testid={`macroarea-save-${m.slug}`}><Check size={13} /></Button>
                <Button size="sm" variant="ghost" onClick={() => setEditSlug(null)} className="h-8 w-8 p-0"><X size={13} /></Button>
              </>
            ) : (
              <>
                <div className="flex-1 flex items-center gap-2 flex-wrap">
                  <div className="font-medium text-slate-900">{m.label}</div>
                  <span className="text-[10px] font-mono uppercase tracking-widest text-slate-500">{m.slug}</span>
                  {m.is_builtin && <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600 border border-slate-200"><LockKey size={10} /> Predefinita</span>}
                </div>
                <button onClick={() => startEdit(m)} className="text-slate-400 hover:text-blue-600 p-1" data-testid={`macroarea-edit-${m.slug}`}><PencilSimple size={14} /></button>
                {m.slug !== "altro" && <button onClick={() => del(m)} className="text-slate-400 hover:text-red-600 p-1" data-testid={`macroarea-delete-${m.slug}`}><Trash size={14} /></button>}
              </>
            )}
          </div>
        ))}
        <div className="flex gap-2 p-3 bg-slate-50">
          <Input placeholder="Nuova macroarea (es. Impianti Elettrici)..." value={newLabel} onChange={(e) => setNewLabel(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), add())} className="text-sm h-9" data-testid="macroarea-new-input" />
          <Button size="sm" onClick={add} data-testid="macroarea-add-btn"><Plus size={14} /> Aggiungi</Button>
        </div>
      </div>
    </Card>
  );
}

